import {
  useCreatePartyContact,
  useDeletePartyContact,
  usePartyContacts,
  useUpdatePartyContact,
} from '@my/directory-api/hooks';
import type {
  ContactTypeValue,
  CreatePartyContactRequest,
  PartyContact,
  UpdatePartyContactRequest,
} from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import { Check, Edit, Mail, Phone, Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PartyContactsSectionProps {
  partyId: string;
}

/**
 * Closed list of contact-type values matching the BE `ContactType` enum.
 * Order is the display order in the dropdown — `Other` is at the bottom
 * because it's the catch-all.
 */
const CONTACT_TYPE_OPTIONS: ContactTypeValue[] = [
  'Primary',
  'Secondary',
  'Billing',
  'Shipping',
  'Dispatcher',
  'Owner',
  'Admin',
  'ItTech',
  'Other',
];

/**
 * Local draft shape — same five mutable fields as the wire body, with
 * `notes` as a non-null string (we coerce to null on save) so the form
 * input can stay un-controlled-friendly.
 */
interface ContactDraft {
  name: string;
  phoneNumber: string;
  email: string;
  notes: string;
  type: ContactTypeValue;
}

const EMPTY_CONTACT_DRAFT: ContactDraft = {
  name: '',
  phoneNumber: '',
  email: '',
  notes: '',
  type: 'Primary',
};

function contactToDraft(c: PartyContact): ContactDraft {
  return {
    name: c.name,
    phoneNumber: c.phoneNumber,
    email: c.email,
    notes: c.notes ?? '',
    type: c.type,
  };
}

/**
 * Card grid + inline editor for a party's contacts. Each card collapses
 * to a one-liner (name + chip badge for type, email + phone summary)
 * and expands inline when the operator clicks the pencil. The "+ Add
 * contact" affordance lives in the section header — clicking it
 * appends a transient draft card pinned to the top of the list.
 *
 * Validation is lightweight: name is required; email + phone are
 * required by the BE but we leave that to the BE validator (the toast
 * surfaces any failure cleanly enough).
 */
export function PartyContactsSection({ partyId }: PartyContactsSectionProps) {
  const { t } = useTranslation(['parties', 'common']);

  const listQuery = usePartyContacts(partyId);
  const createMutation = useCreatePartyContact();
  const updateMutation = useUpdatePartyContact();
  const deleteMutation = useDeletePartyContact();

  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Editor state — `'new'` = drafting a fresh contact pinned to the top;
  // a guid = editing the row with that id; `null` = no editor open.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ContactDraft>(EMPTY_CONTACT_DRAFT);
  const [pendingDelete, setPendingDelete] = useState<PartyContact | null>(null);

  // Pre-resolve i18n labels (typed-keys won't accept template literals).
  const typeLabels: Record<ContactTypeValue, string> = {
    Primary: t('parties:contactType.primary'),
    Secondary: t('parties:contactType.secondary'),
    Billing: t('parties:contactType.billing'),
    Shipping: t('parties:contactType.shipping'),
    Dispatcher: t('parties:contactType.dispatcher'),
    Owner: t('parties:contactType.owner'),
    Admin: t('parties:contactType.admin'),
    ItTech: t('parties:contactType.itTech'),
    Other: t('parties:contactType.other'),
  };
  const typeOptions: SelectInputOption[] = CONTACT_TYPE_OPTIONS.map((value) => ({
    value,
    label: typeLabels[value],
  }));

  const startNew = () => {
    setEditingId('new');
    setDraft(EMPTY_CONTACT_DRAFT);
  };

  const startEdit = (c: PartyContact) => {
    setEditingId(c.id);
    setDraft(contactToDraft(c));
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY_CONTACT_DRAFT);
  };

  const handleSave = async () => {
    const name = draft.name.trim();
    if (!name) return;
    try {
      if (editingId === 'new') {
        const body: CreatePartyContactRequest = {
          partyId,
          name,
          phoneNumber: draft.phoneNumber.trim(),
          email: draft.email.trim(),
          notes: draft.notes.trim() || null,
          type: draft.type,
        };
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: t('parties:contacts.created') });
      } else if (typeof editingId === 'string') {
        const body: UpdatePartyContactRequest = {
          contactId: editingId,
          name,
          phoneNumber: draft.phoneNumber.trim(),
          email: draft.email.trim(),
          notes: draft.notes.trim() || null,
          type: draft.type,
        };
        await updateMutation.mutateAsync({ body, partyId });
        showToast({ type: 'success', message: t('parties:contacts.saved') });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:contacts.saveFailed'),
      });
    }
  };

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    try {
      await deleteMutation.mutateAsync({ id: target.id, partyId });
      showToast({ type: 'success', message: t('parties:contacts.deleted') });
      if (editingId === target.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:contacts.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <section className="rounded-sm border border-slate-300 bg-white p-3 shadow-sm">
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">{t('parties:contacts.title')}</h3>
        {editingId === null && (
          <button
            type="button"
            onClick={startNew}
            disabled={isBusy}
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('common:actions.add')}
          </button>
        )}
      </header>

      {listQuery.isPending && <Loading label={t('parties:contacts.title')} />}
      {listQuery.isError && (
        <ErrorBanner title={t('parties:contacts.loadFailed')} message={listQuery.error.message} />
      )}

      {listQuery.isSuccess && (
        <div className="space-y-2">
          {/* New-contact draft pinned to the top */}
          {editingId === 'new' && (
            <ContactEditor
              draft={draft}
              onChange={setDraft}
              onCancel={cancel}
              onSave={handleSave}
              isBusy={isBusy}
              typeOptions={typeOptions}
            />
          )}

          {(listQuery.data ?? []).length === 0 && editingId !== 'new' ? (
            <p className="px-3 py-4 text-center text-xs italic text-slate-400">
              {t('parties:contacts.empty')}
            </p>
          ) : (
            (listQuery.data ?? []).map((c) =>
              editingId === c.id ? (
                <ContactEditor
                  key={c.id}
                  draft={draft}
                  onChange={setDraft}
                  onCancel={cancel}
                  onSave={handleSave}
                  onDelete={() => setPendingDelete(c)}
                  isBusy={isBusy}
                  typeOptions={typeOptions}
                />
              ) : (
                <ContactCard
                  key={c.id}
                  contact={c}
                  typeLabel={typeLabels[c.type]}
                  rowsLocked={editingId !== null}
                  onEdit={() => startEdit(c)}
                />
              ),
            )
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('parties:contacts.delete.title')}
        message={
          pendingDelete ? t('parties:contacts.delete.message', { name: pendingDelete.name }) : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </section>
  );
}

// ── ContactCard ─────────────────────────────────────────────────────────────

interface ContactCardProps {
  contact: PartyContact;
  typeLabel: string;
  rowsLocked: boolean;
  onEdit: () => void;
}

function ContactCard({ contact, typeLabel, rowsLocked, onEdit }: ContactCardProps) {
  return (
    <div
      className={`flex items-start justify-between gap-2 rounded-md border border-slate-200 bg-white p-3 ${
        rowsLocked ? 'opacity-50' : 'hover:bg-slate-50'
      }`}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-slate-900">{contact.name}</p>
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
            {typeLabel}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          {contact.email && (
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3 w-3" aria-hidden="true" />
              {contact.email}
            </span>
          )}
          {contact.phoneNumber && (
            <span className="inline-flex items-center gap-1">
              <Phone className="h-3 w-3" aria-hidden="true" />
              {contact.phoneNumber}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        aria-label={`Edit ${contact.name}`}
        disabled={rowsLocked}
        onClick={onEdit}
        className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
      >
        <Edit className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// ── ContactEditor ───────────────────────────────────────────────────────────

interface ContactEditorProps {
  draft: ContactDraft;
  onChange: (next: ContactDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Hidden in `new` mode; shown only when editing an existing contact. */
  onDelete?: () => void;
  isBusy: boolean;
  typeOptions: SelectInputOption[];
}

function ContactEditor({
  draft,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
  typeOptions,
}: ContactEditorProps) {
  const { t } = useTranslation(['parties', 'common']);
  const setField = <K extends keyof ContactDraft>(key: K, value: ContactDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const isValid = draft.name.trim().length > 0;

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label={t('parties:contacts.fields.name')}
          name="contact-name"
          value={draft.name}
          maxLength={128}
          onChange={(e) => setField('name', e.target.value)}
          disabled={isBusy}
        />
        <SelectInput
          label={t('parties:contacts.fields.type')}
          name="contact-type"
          value={draft.type}
          options={typeOptions}
          onChange={(e) => setField('type', e.target.value as ContactTypeValue)}
          disabled={isBusy}
        />
        <TextInput
          label={t('parties:contacts.fields.email')}
          name="contact-email"
          type="email"
          value={draft.email}
          maxLength={256}
          onChange={(e) => setField('email', e.target.value)}
          disabled={isBusy}
        />
        <TextInput
          label={t('parties:contacts.fields.phone')}
          name="contact-phone"
          value={draft.phoneNumber}
          maxLength={32}
          onChange={(e) => setField('phoneNumber', e.target.value)}
          disabled={isBusy}
        />
        <div className="md:col-span-2">
          <TextInput
            label={t('parties:contacts.fields.notes')}
            name="contact-notes"
            value={draft.notes}
            maxLength={1024}
            onChange={(e) => setField('notes', e.target.value)}
            disabled={isBusy}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <div>
          {onDelete && (
            <button
              type="button"
              aria-label={t('common:actions.delete')}
              onClick={onDelete}
              disabled={isBusy}
              className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('common:actions.cancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('common:actions.save')}
            onClick={onSave}
            disabled={isBusy || !isValid}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
